import React from 'react'

import clsx from 'clsx'

import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import TagWithHover from 'components/TagWithHover'
import Typography from 'components/Typography'
import UserName from 'components/UserName'
import { formatAvatarInitials, thousandSeparator } from 'helpers/formatters'

import sentiment1SVG from 'assets/ic-sentiment-1.svg'
import sentiment2SVG from 'assets/ic-sentiment-2.svg'
import sentiment3SVG from 'assets/ic-sentiment-3.svg'
import sentiment4SVG from 'assets/ic-sentiment-4.svg'
import sentiment5SVG from 'assets/ic-sentiment-5.svg'

import classes from './UserInfo.module.scss'

const SENTIMENT_ICONS = {
  1: sentiment1SVG,
  2: sentiment2SVG,
  3: sentiment3SVG,
  4: sentiment4SVG,
  5: sentiment5SVG,
}

/** @type {import('./types').UserInfoProps} */
const UserInfo = ({
  isAnonymous = false,
  fullName = '',
  pictureUrl = '',
  isVerifiedReviewer = false,
  jobTitle,
  companyIndustry,
  companySize,
  timeUsed,
  frequencyOfUse,
  sentiment,
  userInfo,
  className,
}) => {
  const showCompanyInfo = companyIndustry || companySize
  const timeUsedValid = timeUsed !== null && timeUsed !== 'Free Trial'
  const showUseFrequency = frequencyOfUse && timeUsedValid
  const onlyEmployee = companySize === 'myself only'
  const onlyEmployeeText = onlyEmployee && companyIndustry ? 'self-employed' : 'Self-employed'

  const avatarContent = isAnonymous ? (
    <div className={classes.userIconBgExp}>
      <Icon icon={'circle-user'} size={24} />
    </div>
  ) : (
    <Avatar src={pictureUrl} className={classes.userAvatar}>
      {formatAvatarInitials(fullName)}
    </Avatar>
  )

  return (
    <div className={clsx('UserInfo', classes.userInfoContainer, className)}>
      <div className={classes.avatarContainer}>
        {avatarContent}
        {sentiment && (
          <Avatar
            data-testid={`sentiment-avatar-${sentiment}`}
            src={SENTIMENT_ICONS[sentiment].src}
            className={classes.sentimentBadge}
          />
        )}
      </div>
      <div className={classes.userDetailsContainer}>
        <UserName userName={fullName} isAnonymous={isAnonymous} isVerified={isVerifiedReviewer} userInfo={userInfo} />
        {isAnonymous && <TagWithHover variant="Requested anonymity" size="small" />}
        {jobTitle && <Typography variant="tight-xs">{jobTitle}</Typography>}
        {showCompanyInfo && (
          <Typography variant="tight-xs">
            {companyIndustry && (
              <span>
                <span>{companyIndustry}</span>
                {thousandSeparator(companySize) && <span>, </span>}
              </span>
            )}
            {companySize && (
              <span>{onlyEmployee ? onlyEmployeeText : `${thousandSeparator(companySize)} employees`}</span>
            )}
          </Typography>
        )}
        {showUseFrequency && (
          <Typography variant="tight-xs">
            Used {frequencyOfUse} for {timeUsed.toLowerCase()}
          </Typography>
        )}
      </div>
    </div>
  )
}

export default UserInfo
